import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import MainLayout from '../../../layouts/MainLayout';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services';
import { getPedidosByDepartamento } from '../config';
import { getContextoAcesso, isPedidoVisivelParaUsuario } from '../core/services/acessoPedidoService';
import { FaFileAlt, FaArrowLeft, FaChevronRight } from 'react-icons/fa';

const PageContainer = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
`;

const Container = styled.div`
  padding: 120px 2rem 3rem 2rem;
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1rem 2rem 1rem;
  }
`;

const TopBar = styled.div`
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;

  @media (max-width: ${breakpoints.small}) {
    gap: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  color: ${colors.darkBlue};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  white-space: nowrap;

  &:hover {
    background: ${colors.lightBlue};
    border-color: ${colors.lightBlue};
    color: white;
    transform: translateX(-4px);
    box-shadow: 0 4px 12px rgba(37, 99, 128, 0.15);
  }
`;

const PageTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
  overflow-wrap: anywhere;

  @media (max-width: ${breakpoints.small}) {
    font-size: 1.15rem;
  }
`;

const PedidosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  gap: 1.5rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const PedidoCard = styled.div`
  background: white;
  border-radius: 25px;
  padding: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.lightBlue}, ${colors.darkBlue});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 30px -12px rgba(0, 0, 0, 0.15);
    border-color: rgba(37, 99, 128, 0.2);

    &::before {
      transform: scaleX(1);
    }

    .arrow-icon {
      transform: translateX(4px);
      opacity: 1;
    }
  }
`;

const PedidoIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 128, 0.1) 0%, rgba(37, 99, 128, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;

  svg {
    font-size: 28px;
    color: ${colors.lightBlue};
  }

  ${PedidoCard}:hover & {
    background: linear-gradient(135deg, ${colors.lightBlue} 0%, ${colors.darkBlue} 100%);

    svg {
      color: white;
    }
  }
`;

const PedidoTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const PedidoDescription = styled.p`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const PedidoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const ArrowIcon = styled(FaChevronRight)`
  font-size: 0.875rem;
  color: ${colors.lightBlue};
  opacity: 0.6;
  transition: all 0.3s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 25px;
  border: 2px dashed #e1e4e8;

  svg {
    font-size: 4rem;
    color: ${colors.lightBlue};
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    color: ${colors.textMuted};
    font-size: 1rem;
  }
`;

/**
 * Página de listagem de pedidos de um departamento "simples" (sem categorias),
 * reaproveitada por Comissões, Atualização Cadastral e Certidões — mesmo
 * layout/comportamento, só muda o departamento, o título e o ícone.
 *
 * @param {string} departamentoId - id do departamento em config/index.js
 * @param {string} titulo - texto ao lado do ícone no cabeçalho (ex: "Comissões da OAB/MS")
 * @param {string} [nomeEmptyState] - nome usado na mensagem de "nenhum pedido disponível"
 *   (ex: "Comissões"); default: igual a `titulo`, pros casos em que os dois já batem
 * @param {React.ComponentType} Icon - ícone do react-icons (cabeçalho e cada card)
 */
const DepartamentoPedidosPage = ({ departamentoId, titulo, nomeEmptyState, Icon }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const contextoAcesso = useMemo(() => getContextoAcesso(user, authService.getOABData()), [user]);
  const pedidos = useMemo(
    () => getPedidosByDepartamento(departamentoId)
      .filter(p => isPedidoVisivelParaUsuario(p, contextoAcesso))
      .map(p => (contextoAcesso.tipoInscricao === 'PJ' && p.descricaoPJ ? { ...p, descricao: p.descricaoPJ } : p)),
    [departamentoId, contextoAcesso]
  );

  const handleBackToHome = () => navigate('/home');

  return (
    <MainLayout>
      <PageContainer>
        <Container>
          <TopBar>
            <BackButton onClick={handleBackToHome}>
              <FaArrowLeft /> Voltar ao Início
            </BackButton>
            <PageTitle><Icon /> {titulo}</PageTitle>
          </TopBar>

          {pedidos.length === 0 ? (
            <EmptyState>
              <FaFileAlt />
              <p>Nenhum pedido de {nomeEmptyState || titulo} disponível para o seu perfil no momento</p>
            </EmptyState>
          ) : (
            <PedidosGrid>
              {pedidos.map((pedido, index) => (
                <PedidoCard
                  key={pedido.id}
                  onClick={() => navigate(`/requerimentos/${departamentoId}/${pedido.id}`)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <PedidoIconWrapper>
                    <Icon />
                  </PedidoIconWrapper>
                  <PedidoTitle>{pedido.nome}</PedidoTitle>
                  <PedidoDescription>{pedido.descricao}</PedidoDescription>
                  <PedidoFooter>
                    <ArrowIcon className="arrow-icon" />
                  </PedidoFooter>
                </PedidoCard>
              ))}
            </PedidosGrid>
          )}
        </Container>
      </PageContainer>
    </MainLayout>
  );
};

export default DepartamentoPedidosPage;
